import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { submitContact } from '../apis/contactApis';
import Loader from '../Components/Loader/Loader';
import { Helmet } from 'react-helmet-async';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  service: '',
  message: '',
};

const initialErrors = {
  firstName: '',
  email: '',
  phoneNumber: '',
  message: '',
};

function ContactUs() {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors = { ...initialErrors };
    let isValid = true;

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Name is required.';
      isValid = false;
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'Name must be at least 2 characters.';
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
      isValid = false;
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Enter a valid email address.';
      isValid = false;
    }

    const phoneRegex = /^[0-9]{7,15}$/;
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required.';
      isValid = false;
    } else if (!phoneRegex.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Enter a valid phone number (7-15 digits).';
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required.';
      isValid = false;
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await submitContact(formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData(initialForm);
      setErrors(initialErrors);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send message. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}

<Helmet>
    <title>
        Contact Al Ibtekar | Cable Management Solutions UAE
    </title>

    <meta
        name="description"
        content="Contact Al Ibtekar in Sharjah, UAE for cable management, cable termination, electrical enclosures, cable accessories and E&I solutions. Request a quote or speak with our technical team."
    />

    <meta
        name="keywords"
        content="contact Al Ibtekar, cable management UAE, cable termination UAE, electrical solutions UAE, electrical enclosures UAE, cable accessories UAE, E&I solutions UAE, cable management company Sharjah, electrical company Sharjah"
    />

    <link
        rel="canonical"
        href="https://saig.me/contact"
    />

    <meta
        property="og:title"
        content="Contact Al Ibtekar | Cable Management Solutions UAE"
    />

    <meta
        property="og:description"
        content="Get in touch with Al Ibtekar for cable management, cable termination, electrical enclosures and industrial electrical solutions in the UAE."
    />

    <meta
        property="og:url"
        content="https://saig.me/contact"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:site_name"
        content="Al Ibtekar"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Contact Al Ibtekar | Cable Management Solutions UAE"
    />

    <meta
        name="twitter:description"
        content="Contact Al Ibtekar in Sharjah, UAE for cable management, cable termination and electrical solutions. Request a quote today."
    />
</Helmet>
      {/* Banner Section */}
      <div className="mt-[91px] overflow-hidden relative">
        <img src="/assets/contact/bg1.png" className="w-full lg:h-auto h-[300px] object-cover" alt="Trusted cable management and termination solutions provider in the UAE" />
        <div
          className="absolute lg:top-[125px] top-[20px] lg:px-[50px] px-[20px] max-w-[1440px] w-full mx-auto left-1/2 -translate-x-1/2">
          <motion.h6
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }} className="font-[500] font-kanit lg:text-[70px] text-[30px] lg:leading-[90px] leading-[40px] text-[#FFFFFF] lg:pb-[14px] pb-[4px]">
            Let's Get In Touch With Us
          </motion.h6>
        </div>
        <motion.h1
          className="absolute font-[600] font-kanit lg:text-[180px] text-[60px] lg:leading-[180px] leading-[70px] text-[#FFFFFF4F] right-[0px] lg:bottom-[86px] bottom-[40px]"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Contact
        </motion.h1>
      </div>

      {/* Contact Info Section */}
      <motion.section
        className="lg:py-[115px] py-[60px] relative"
        style={{
          background: `radial-gradient(96.62% 164.95% at 90% 40%, rgba(228, 74, 26, 0.08) 0%, rgba(228, 74, 26, 0) 13%), radial-gradient(86.33% 147.39% at 25% 73%, rgba(228, 74, 26, 0.18) 0%, rgba(228, 74, 26, 0) 29%)`,
        }}
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute lg:top-[60px] top-[0px] right-[50px]">
          <img src="/assets/home/wrap2.svg" alt="Trusted cable management and termination solutions provider in the UAE" />
        </div>
        <div className='max-w-[1440px] w-full mx-auto lg:px-[50px] px-[20px]'>
          <motion.p
            className="text-center text-[14px] uppercase tracking-[2px] text-[#FBB210] pb-[15px] font-[400] font-kanit"
            variants={fadeInUp}
          >
            Get To Know Us
          </motion.p>
          <motion.h6
            className="text-center font-[500] font-inter lg:text-[42px] text-[26px] text-[#0F0F0F] lg:pb-[130px] pb-[70px]"
            variants={fadeInUp}
          >
            Have a Project In Mind? Get In Touch!
          </motion.h6>
          <div className="grid lg:grid-cols-3 grid-cols-1 lg:gap-[30px] gap-[40px]">
            {['call', 'msg', 'lctn'].map((icon, i) => (
              <motion.div
                key={icon}
                variants={fadeInUp}
                transition={{ delay: 0.2 * i }}
                className="bg-white rounded-[10px] pb-[45px] px-[30px] shadow-lg"
              >
                <img src={`/assets/contact/${icon}.svg`} className="mt-[-34px] mb-[41px]" alt="" />
                <h6 className="text-[24px] font-[500] font-kanit text-[#000] pb-[14px]">
                  {icon === 'call' ? 'Have any question?' : icon === 'msg' ? 'Write email' : 'Visit anytime'}
                </h6>
                <p className="text-[18px] font-[300] font-kanit text-[#49577A]">
                  {icon === 'call'
                    ? 'Free +971 6 555 6624'
                    : icon === 'msg'
                      ? 'info@saig.me'
                      : 'PO Box 513347, Ind Area 18 Sharjah, UAE'}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

      </motion.section>

      {/* Quote Form Section */}
      <motion.section
        className="service-section lg:pt-[114px] pt-[60px] lg:pb-[105px] pb-[60px]"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
      >
        <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-[40px] gap-[20px] max-w-[1440px] w-full mx-auto lg:px-[50px] px-[0px]">
          <motion.div variants={fadeInUp}>
            <img style={{ height: '100%',objectFit:'cover',borderRadius:'20px' }} src="/assets/service/contact.jpg" alt="" />
          </motion.div>

          <motion.div className="bg-white rounded-[10px] pt-[35px] lg:px-[58px] px-[20px] pb-[22px]" variants={fadeInUp}>
            <form onSubmit={handleSubmit}>
              <p className="text-[#FBB210] text-[20px] font-kanit uppercase tracking-[2px] pb-[15px]">Get A Quote</p>
              <h6 className="font-kanit font-[500] lg:text-[52px] text-[30px] text-[#000] lg:leading-[60px] pb-[53px]">
                Let’s Stay Connected With Us
              </h6>
              <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-[30px] gap-y-[23px]">

                <div className="flex flex-col gap-1">
                  <input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name *"
                    className={`border rounded-[10px] py-[19px] px-[22px] font-kanit text-[17px] ${errors.firstName ? 'border-red-500' : 'border-[#0922101A]'}`}
                  />
                  {errors.firstName && <span className="text-red-500 text-[13px] px-1">{errors.firstName}</span>}
                </div>

                <div className="flex flex-col gap-1">
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    className="border border-[#0922101A] rounded-[10px] py-[19px] px-[22px] font-kanit text-[17px]"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address *"
                    type="email"
                    className={`border rounded-[10px] py-[19px] px-[22px] font-kanit text-[17px] ${errors.email ? 'border-red-500' : 'border-[#0922101A]'}`}
                  />
                  {errors.email && <span className="text-red-500 text-[13px] px-1">{errors.email}</span>}
                </div>

                <div className="flex flex-col gap-1">
                  <input
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="Phone Number *"
                    className={`border rounded-[10px] py-[19px] px-[22px] font-kanit text-[17px] ${errors.phoneNumber ? 'border-red-500' : 'border-[#0922101A]'}`}
                  />
                  {errors.phoneNumber && <span className="text-red-500 text-[13px] px-1">{errors.phoneNumber}</span>}
                </div>

                <div className="lg:col-span-2 flex flex-col gap-1">
                  <input
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    placeholder="Service Type"
                    className="border border-[#0922101A] rounded-[10px] py-[19px] px-[22px] font-kanit text-[17px]"
                  />
                </div>

                <div className="lg:col-span-2 flex flex-col gap-1">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full border rounded-[10px] py-[19px] px-[22px] font-kanit text-[17px] ${errors.message ? 'border-red-500' : 'border-[#0922101A]'}`}
                    placeholder="Message *"
                    rows="3"
                  ></textarea>
                  {errors.message && <span className="text-red-500 text-[13px] px-1">{errors.message}</span>}
                </div>

              </div>
              <button
                type="submit"
                disabled={loading}
                className="py-[15px] px-[35px] rounded-[50px] font-[500] font-kanit text-[15px] leading-[24px] mt-[20px] mb-[16px] bg-[#FBB210] hover:bg-transparent border border-transparent hover:border-[#FBB210] transition-all ease-in text-[#FFFFFF] hover:text-[#FBB210] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Submit Now'}
              </button>
            </form>

            <div className="flex lg:items-center items-start lg:flex-row flex-col gap-[26px]">
              <p className="max-w-[268px] font-kanit text-[17px] text-[#666]">If you have any questions, contact our office at:</p>
              <div className="flex items-center gap-[12px]">
                <img src="/assets/contact/Background.svg" alt="" />
                <p className="font-kanit text-[20px] text-[#000]">+971 6 555 6624</p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Location Section */}
      <motion.section
        className="lg:pt-[100px] pt-[60px] lg:pb-[150px] pb-[60px]"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
      >
        <div className='max-w-[1440px] w-full mx-auto lg:px-[50px] px-[20px]s'>
          <p className="font-[400] font-kanit text-[20px] text-center leading-[20px] uppercase tracking-[2px] text-[#FBB210] pb-[15px]">
            Location
          </p>
          <h6 className="font-[500] font-kanit lg:text-[52px] text-[30px] text-center lg:leading-[60px] leading-[40px] text-[#000] pb-[70px]">
            Our Location
          </h6>

          <iframe
            className="map w-full h-[400px] rounded-[10px]"
            src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3608.7833617040706!2d55.54537577538333!3d25.244220677680687!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjXCsDE0JzM5LjIiTiA1NcKwMzInNTIuNiJF!5e0!3m2!1sen!2sin!4v1770279348695!5m2!1sen!2sin"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Our Location"
          ></iframe>
        </div>
      </motion.section>
    </>
  );
}

export default ContactUs;
